import { Table as BaseTable } from 'antd';
import { type SizeType } from 'antd/lib/config-provider/SizeContext';
import { type ColumnProps } from 'antd/lib/table';
import React from 'react';

import TableHeader from './TableHeader';
import TableWrapper, { type TableVariant } from './TableWrapper';

const DEFAULT_PAGINATION_OPTIONS = {
  defaultPageSize: 20,
  pageSizeOptions: ['10', '20', '30', '40', '50', '75', '100'],
  showSizeChanger: true,
};

export interface TableProps<T> {
  className?: string;
  columnsDefinition: ColumnProps<T>[];
  data: T[];
  headerButtons?: React.ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
  noDataLabel?: string;
  onRowClick?: (rowData: T) => void;
  onRowMiddleClick?: (rowData: T) => void;
  size?: SizeType;
  title: string;
  variant?: TableVariant;
}

function isAlternateMouseEvent(e: React.MouseEvent) {
  return e.button === 1 || e.ctrlKey || e.metaKey;
}

function Table<T extends object>(props: TableProps<T>) {
  const {
    columnsDefinition,
    data,
    headerButtons,
    isLoading,
    onRowClick,
    onRowMiddleClick,
    size,
    title,
    ...tableWrapperProps
  } = props;

  return (
    <TableWrapper isLoading={isLoading} {...tableWrapperProps}>
      <TableHeader size={size} actionButtons={headerButtons} title={title} />

      <BaseTable
        columns={columnsDefinition}
        dataSource={data}
        loading={isLoading}
        pagination={DEFAULT_PAGINATION_OPTIONS}
        size={size}
        onRow={(record: T) => ({
          onClick: () => {},
          onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
          onMouseUp: (e: React.MouseEvent) =>
            isAlternateMouseEvent(e)
              ? onRowMiddleClick?.(record)
              : onRowClick?.(record),
        })}
      />
    </TableWrapper>
  );
}

export default Table;
